// iter462 — Sales-vs-Bank reconciliation widget on the Sales page.
// Compact, opt-in card that compares each day's POS bank-sales to the
// bank credit lines that landed `lag_days` later (default T+1, the
// typical KSA settlement window). User toggles it on/off via the
// header pill — preference persists in localStorage.
//
// Daily detail can be expanded; the monthly summary is always shown
// when the widget is on. Days with non-zero variance are highlighted.
import { cur } from '@/lib/currency';
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, RefreshCw, X, Banknote, ArrowUp, ArrowDown } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import SalesVsBankMonthly from './SalesVsBankMonthly';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SalesVsBankWidget({ startDate, endDate, branchId }) {
  // Persist enable/disable so this stays opt-in across sessions. The
  // user explicitly asked for a master toggle "so we can on and off
  // any time we want".
  const LS_KEY = 'smarto.sales-vs-bank.enabled';
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, enabled ? '1' : '0'); } catch { /* noop */ }
  }, [enabled]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lagDays, setLagDays] = useState(1);
  const [expandDaily, setExpandDaily] = useState(false);
  const [view, setView] = useState('daily');

  const load = async () => {
    setLoading(true);
    try {
      const params = { lag_days: lagDays };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (branchId) params.branch_id = branchId;
      const r = await api.get('/sales/vs-bank', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load sales-vs-bank');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (enabled) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, startDate, endDate, branchId, lagDays]);

  const summary = data?.monthly_summary || {};
  const daily = data?.daily || [];

  // Top 3 worst-variance days for the "at a glance" section.
  const worstDays = useMemo(() => {
    return [...daily]
      .filter(d => Math.abs(d.variance) > 0.5)
      .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
      .slice(0, 3);
  }, [daily]);

  if (!enabled) {
    return (
      <div className="flex items-center justify-end" data-testid="sales-vs-bank-master-toggle-off">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEnabled(true)}
          className="h-7 text-[11px] gap-1"
          data-testid="sales-vs-bank-enable-btn"
        >
          <Banknote size={12} />
          Enable Sales vs Bank check
        </Button>
      </div>
    );
  }

  const varSign = summary.variance >= 0 ? '+' : '';
  const varColor = summary.variance === 0 ? 'text-muted-foreground'
    : summary.variance > 0 ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-rose-600 dark:text-rose-400';

  return (
    <Card className="border-info/40 bg-gradient-to-br from-blue-50/30 to-white dark:from-blue-900/10 dark:to-background" data-testid="sales-vs-bank-widget">
      <CardContent className="p-4 space-y-3">
        {/* Header strip — toggle, lag-days, refresh, close. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Banknote size={16} className="text-info" />
            <span className="font-semibold text-sm">Sales vs Bank</span>
            <Badge variant="outline" className="text-[10px]">
              T+{lagDays} settlement
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border overflow-hidden text-[11px]" data-testid="sales-vs-bank-view-toggle">
              {[['daily', 'Daily by branch'], ['monthly', 'Monthly by bank']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setView(k)} data-testid={`sales-vs-bank-view-${k}`}
                  className={`px-2 h-7 transition-colors ${view === k ? 'bg-info text-white' : 'bg-background hover:bg-muted'}`}>{l}</button>
              ))}
            </div>
            <select
              value={lagDays}
              onChange={(e) => setLagDays(Number(e.target.value))}
              className="h-7 px-2 text-[11px] border rounded-md bg-background"
              data-testid="sales-vs-bank-lag"
              title="POS settlement lag in days"
            >
              <option value={0}>Same-day</option>
              <option value={1}>T+1 (next day)</option>
              <option value={2}>T+2</option>
              <option value={3}>T+3</option>
            </select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-7 px-2" data-testid="sales-vs-bank-refresh">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEnabled(false)} className="h-7 px-2" data-testid="sales-vs-bank-close-btn" title="Turn off — re-enable from header pill">
              <X size={12} />
            </Button>
          </div>
        </div>

        {view === 'monthly' ? <SalesVsBankMonthly lagDays={lagDays} /> : <>
        {/* Monthly summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="sales-vs-bank-summary">
          <Tile label="POS bank sales" value={`${cur()} ${fmt(summary.pos_total)}`} />
          <Tile label="Bank credited" value={`${cur()} ${fmt(summary.bank_total)}`} />
          {/* iter464 — Refund tile is only shown when there's something to show */}
          {(summary.refund_total || 0) > 0 && (
            <Tile
              label="− Refunds"
              value={`${cur()} ${fmt(summary.refund_total)}`}
              valueClass="text-rose-600 dark:text-rose-400"
            />
          )}
          <Tile
            label="Variance"
            value={`${varSign}${cur()} ${fmt(Math.abs(summary.variance || 0))}`}
            valueClass={varColor}
            icon={summary.variance > 0 ? <ArrowUp size={11} /> : summary.variance < 0 ? <ArrowDown size={11} /> : null}
          />
          <Tile label="Matched" value={`${(summary.matched_pct ?? 100).toFixed(1)}%`} subtext={`${summary.days_with_variance || 0} day${summary.days_with_variance === 1 ? '' : 's'} with variance`} />
        </div>

        {/* iter463 — Hint when user-flagged POS-settlement codes are counted */}
        {(data?.user_pos_codes_count > 0 || data?.user_refund_codes_count > 0) && (
          <div className="text-[10px] text-muted-foreground" data-testid="sales-vs-bank-user-codes-hint">
            Counting{' '}
            {data?.user_pos_codes_count > 0 && (
              <>
                <span className="font-semibold text-info">{data.user_pos_codes_count}</span> POS code{data.user_pos_codes_count === 1 ? '' : 's'}
              </>
            )}
            {data?.user_pos_codes_count > 0 && data?.user_refund_codes_count > 0 && ' · '}
            {data?.user_refund_codes_count > 0 && (
              <>
                <span className="font-semibold text-rose-600">{data.user_refund_codes_count}</span> refund code{data.user_refund_codes_count === 1 ? '' : 's'}
              </>
            )}
            {' '}from your <span className="font-medium">Bank Code Dictionary</span>.
          </div>
        )}

        {/* Worst-day highlights */}
        {worstDays.length > 0 && (
          <div className="rounded-lg border border-warning/30 bg-warning/5 p-2.5 text-[11px] space-y-1" data-testid="sales-vs-bank-worst">
            <div className="font-semibold text-warning">
              Top {worstDays.length} day{worstDays.length === 1 ? '' : 's'} to investigate
            </div>
            {worstDays.map((d) => (
              <div key={`${d.date}-${d.branch_id}`} className="flex items-center justify-between">
                <span className="text-muted-foreground">{d.date} · {d.branch_name}</span>
                <span className={d.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                  {d.variance >= 0 ? '+' : ''}SR {fmt(Math.abs(d.variance))}
                  <span className="text-muted-foreground ml-1">
                    (POS {fmt(d.pos_bank_sales)} · Bank {fmt(d.bank_credit)})
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Daily breakdown (collapsible) */}
        <div>
          <button
            type="button"
            onClick={() => setExpandDaily(!expandDaily)}
            className="text-[11px] text-info hover:underline inline-flex items-center gap-1"
            data-testid="sales-vs-bank-expand-daily"
          >
            {expandDaily ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            {expandDaily ? 'Hide' : 'Show'} daily breakdown ({daily.length} row{daily.length === 1 ? '' : 's'})
          </button>
          {expandDaily && (
            <div className="mt-2 max-h-64 overflow-auto rounded-lg border" data-testid="sales-vs-bank-daily-table">
              <table className="w-full text-[11px]">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-1.5">Date</th>
                    <th className="text-left p-1.5">Branch</th>
                    <th className="text-right p-1.5">POS bank</th>
                    <th className="text-right p-1.5">Bank credit</th>
                    <th className="text-right p-1.5">− Refunds</th>
                    <th className="text-right p-1.5">Variance</th>
                    <th className="text-right p-1.5">%</th>
                  </tr>
                </thead>
                <tbody>
                  {daily.length === 0 && (
                    <tr><td colSpan={7} className="text-center p-3 text-muted-foreground">No data in this range</td></tr>
                  )}
                  {daily.map((d) => (
                    <tr key={`${d.date}-${d.branch_id}`} className={Math.abs(d.variance) > 0.5 ? 'bg-warning/5' : ''}>
                      <td className="p-1.5 font-mono">{d.date}</td>
                      <td className="p-1.5">{d.branch_name}</td>
                      <td className="p-1.5 text-right">{fmt(d.pos_bank_sales)}</td>
                      <td className="p-1.5 text-right">{fmt(d.bank_credit)}</td>
                      <td className="p-1.5 text-right text-rose-600">{d.refund_credit ? fmt(d.refund_credit) : '—'}</td>
                      <td className={`p-1.5 text-right font-semibold ${d.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {d.variance >= 0 ? '+' : ''}{fmt(d.variance)}
                      </td>
                      <td className="p-1.5 text-right text-muted-foreground">{d.matched_pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>}
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, valueClass = '', subtext, icon }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm font-bold mt-0.5 flex items-center gap-1 ${valueClass}`}>{icon}{value}</div>
      {subtext && <div className="text-[10px] text-muted-foreground mt-0.5">{subtext}</div>}
    </div>
  );
}
